import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { QueryTarget } from "@/shared/domain/query";
import { useRoboqlExternalCtx } from "@/shared/domain/query/hooks";
import { Field } from "@/shared/foundation/primitives";

import styles from "./invocationSpec.module.css";

/**
 * Monaco is a large dependency and most invocations name no query at all, so
 * it is fetched when a query row is added rather than with the form.
 */
const RoboqlEditor = React.lazy(() =>
  import("@/shared/components/RoboqlEditor").then((module) => ({
    default: module.RoboqlEditor,
  })),
);

export interface RoboqlQueryFieldProps {
  /** Where the query text lives, e.g. `targets.0.spec.inputs.2.text`. */
  name: string;
}

/**
 * The RoboQL a query input mounts its files from.
 *
 * The editor completes against the org's own files -- metadata keys, tags --
 * which is the reason it is here instead of a textarea, so it is targeted at
 * `Files`: the selector this row becomes is a file selector, and completions
 * offered against anything else would name fields the query cannot use.
 *
 * It is seeded once, from the value the form mounts with. The editor owns the
 * text from then on and reports every keystroke back, so nothing needs to be
 * written into it; a row that arrives later is a new row, and `useFieldArray`
 * mounts it fresh.
 */
export function RoboqlQueryField({ name }: RoboqlQueryFieldProps) {
  const { control } = useFormContext();
  const orgId = useCurrentOrgId();
  const externalCtx = useRoboqlExternalCtx(QueryTarget.Files, orgId);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field.Root invalid={fieldState.invalid}>
          {/* Wears the `Textarea` frame, and carries the focus ring itself:
              Monaco focuses a textarea of its own well inside this box, so
              `:focus-within` is what knows the field is being typed in. */}
          <div
            className={styles.queryEditor}
            data-invalid={fieldState.invalid || undefined}
            data-testid="roboql-query-editor"
          >
            <React.Suspense
              fallback={<div className={styles.queryEditorFallback} />}
            >
              <RoboqlEditor
                target={QueryTarget.Files}
                roboqlExternalCtx={externalCtx}
                initialValue={
                  typeof field.value === "string" ? field.value : ""
                }
                onChange={(value) => field.onChange(value ?? "")}
                // The row is one field among many on a form the author is
                // working down; it must not open a suggestion list the moment
                // it appears.
                triggerCompletionOnEmptyInitialValue={false}
                containerClassName={styles.queryEditorContainer}
                editorOptions={{
                  ariaLabel: "RoboQL query",
                  placeholder: "tags CONTAINS 'harbor'",
                  padding: { top: 6, bottom: 6 },
                  // Long queries wrap, so only the vertical scrollbar can
                  // ever be wanted, and only once there is something to
                  // scroll.
                  scrollbar: {
                    vertical: "auto",
                    horizontal: "hidden",
                    useShadows: false,
                  },
                }}
              />
            </React.Suspense>
          </div>
          <Field.Description>
            Resolved when the invocation runs. A value taken from the firing
            must sit inside a quoted string.
          </Field.Description>
          <Field.Error match>{fieldState.error?.message}</Field.Error>
        </Field.Root>
      )}
    />
  );
}
